import { Entity } from '@backstage/catalog-model';
import { createApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';

import { UnifiedGithubApi } from '@runway/devkit';

export interface DeleteArgoVars {
  [k: string]: string | object;
  githubOwner: string;
  githubRepo: string;
}
export interface DeleteGithubRepoVars {
  [k: string]: string | object;
  githubOwner: string;
  githubRepo: string;
  action: string;
}

export type AaTemplate = {
  cmdbAppShortName: string;
  squads: string[];
  apiVersion: string;
  intent?: string;
};

export type RepoCustomProperties = {
  appShortName: string;
  squadID: string[];
  intent: string;
};

export interface CatalogProjectsServiceApi {
  deleteGithubRepo(vars: DeleteGithubRepoVars, entity: Entity): Promise<void>;
  getAaYaml(entity: Entity): Promise<AaTemplate>;
  getRepoCustomProperties(entity: Entity): Promise<RepoCustomProperties>;
}

export const catalogProjectsServiceApiRef = createApiRef<CatalogProjectsServiceApi>({
  id: 'plugin.catalog-customizations.backend.service',
});

export class CatalogProjectsService implements CatalogProjectsServiceApi {
  constructor(private readonly unifiedGithubApi: UnifiedGithubApi) {}

  deleteGithubRepo = async ({ ...vars }: DeleteGithubRepoVars, entity: Entity) => {
    const queryStringVars = vars as Record<string, string>;
    const { githubOwner, githubRepo, action } = queryStringVars;

    const missingQueryParams = Object.entries({ githubOwner, githubRepo, action })
      .filter(([, value]) => !value)
      .map(([varName]) => varName);

    if (missingQueryParams.length > 0) {
      throw new Error(`Missing required query params: ${missingQueryParams}`);
    }

    const username = await this.unifiedGithubApi.getCurrentUsernameForEntity({ entity });
    const permission = await this.unifiedGithubApi
      .getCollaboratorPermissionLevelForEntity({ entity, username })
      .catch(() => '');

    if (permission !== 'admin') {
      throw new Error(`User is not authorized to perform these actions on ${githubOwner}/${githubRepo}`);
    }

    if (action === 'archive') {
      try {
        await this.unifiedGithubApi.archiveRepoForEntity({ entity });
        return;
      } catch (error: any) {
        if (error.toString().includes('Repository was archived so is read-only')) {
          throw new Error('GitHub repository was already archived. Cannot re-archive');
        }
        throw new Error('Error archiving GitHub repository');
      }
    }

    if (action === 'delete') {
      try {
        await this.unifiedGithubApi.deleteRepoForEntity({ entity });
        return;
      } catch (error: any) {
        if (error.toString().includes('Not Found')) {
          throw new Error('Was this GitHub repo already deleted? We cannot find it');
        }
        throw new Error('Error deleting GitHub repository');
      }
    }
  };

  private loadAaYaml = (templateStr: string | undefined, repoName: string): AaTemplate => {
    if (!templateStr) {
      return { cmdbAppShortName: '', squads: [], apiVersion: '' };
    }
    try {
      const templateAaYaml: any = yaml.load(templateStr);

      if (templateAaYaml.squad) {
        if (!templateAaYaml.squads) {
          templateAaYaml.squads = [templateAaYaml.squad];
        }
        delete templateAaYaml.squad;
      }

      if (typeof templateAaYaml.squads === 'string') {
        templateAaYaml.squads = [templateAaYaml.squads];
      }

      if (templateAaYaml.archerShortname) {
        if (!templateAaYaml.cmdbAppShortName) {
          templateAaYaml.cmdbAppShortName = templateAaYaml.archerShortname;
        }
        delete templateAaYaml.archerShortname;
      }

      if (templateAaYaml.applicationShortname) {
        if (!templateAaYaml.cmdbAppShortName) {
          templateAaYaml.cmdbAppShortName = templateAaYaml.applicationShortname;
        }
        delete templateAaYaml.applicationShortname;
      }

      return templateAaYaml;
    } catch (err: any) {
      throw new Error(`load aa.yaml for ${repoName}`);
    }
  };

  getAaYaml = async (entity: Entity): Promise<AaTemplate> => {
    const slug = this.unifiedGithubApi.getProjectSlugForEntity({ entity });
    const repoName = slug?.split('/')[1] ?? entity.metadata.name;
    const text = await this.unifiedGithubApi.fetchFileContentForEntity({ entity, filePath: 'aa.yaml' });
    return this.loadAaYaml(text, repoName);
  };

  getRepoCustomProperties = async (entity: Entity): Promise<RepoCustomProperties> => {
    const properties: RepoCustomProperties = { appShortName: '', squadID: [], intent: '' };

    const rawProperties = await this.unifiedGithubApi.listRepoCustomPropertiesForEntity({ entity });

    for (const element of rawProperties) {
      if (element.property_name === 'app-shortname') {
        properties.appShortName = element.value;
      }
      if (element.property_name === 'squad-id') {
        properties.squadID = element.value.split(',').map((s: string) => s.trim());
      }
      if (element.property_name === 'intent') {
        properties.intent = element.value;
      }
    }

    return properties;
  };
}
